/**
 * 💜 HEARTBEATS PORTAL - Portail dédié heartbeats.fr
 * 
 * Page autonome pour les utilisateurs venant de heartbeats.fr
 * Affiche UNIQUEMENT le module Heartbeats (pas l'espace client LPE)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, LogOut, Settings, User, Shield, ArrowLeft, Globe } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import HeartbeatsModule from '../components/HeartbeatsModule';
import { LanguageSelector } from '../components/LanguageSelector';
import { api } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clés localStorage
const TOKEN_KEY = 'heartbeats_token';
const USER_KEY = 'heartbeats_user';

const HeartbeatsPortal = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    let authComplete = false;
    let isMounted = true;
    
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const savedUser = localStorage.getItem(USER_KEY);
        
        console.log('🔐 HeartbeatsPortal: Vérification auth...', { hasToken: !!token, hasSavedUser: !!savedUser });
        
        if (!token) {
          console.log('🔐 Pas de token, redirection vers login');
          window.location.href = '/auth?mode=login';
          return;
        }

        // Essayer de charger le profil depuis le localStorage d'abord (fallback immédiat)
        let cachedUser = null;
        if (savedUser) {
          try {
            cachedUser = JSON.parse(savedUser);
            if (isMounted) setCustomer(cachedUser);
            console.log('🔐 Cache utilisateur chargé:', cachedUser?.id || cachedUser?.email);
          } catch (e) {
            console.error('🔐 Erreur parsing savedUser:', e);
          }
        }

        // Valider le token avec l'API avec timeout de 5 secondes
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        try {
          const res = await fetch(`${API_URL}/api/heartbeats/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          if (!res.ok) {
            console.log('🔐 Token invalide (HTTP', res.status, '), redirection vers login...');
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            window.location.href = '/auth?mode=login';
            return;
          }

          const userData = await res.json();
          console.log('🔐 Utilisateur validé:', userData?.id || userData?.email);
          
          // 🔧 FIX: Nettoyer les données LPE obsolètes qui peuvent causer des conflits
          localStorage.removeItem('lpep_customer');
          localStorage.removeItem('lpep_token');
          
          // Stocker les données utilisateur fraîches
          localStorage.setItem(USER_KEY, JSON.stringify(userData));
          if (isMounted) {
            setCustomer(userData);
            authComplete = true;
          }
        } catch (fetchError) {
          // Timeout ou erreur réseau sur le fetch
          console.log('🔐 Erreur fetch API (timeout ou réseau):', fetchError.name);
          clearTimeout(timeoutId);
          
          // Utiliser le cache si disponible
          if (cachedUser && isMounted) {
            console.log('🔐 Utilisation du cache suite à erreur réseau');
            setCustomer(cachedUser);
            authComplete = true;
          } else {
            throw fetchError; // Re-throw si pas de cache
          }
        }
        
      } catch (error) {
        console.error('🔐 Erreur auth globale:', error);
        // En cas d'erreur réseau, utiliser les données en cache si disponibles
        const savedUser = localStorage.getItem(USER_KEY);
        if (savedUser) {
          try {
            if (isMounted) {
              setCustomer(JSON.parse(savedUser));
              authComplete = true;
            }
            console.log('🔐 Fallback sur cache après erreur');
          } catch (e) {
            console.log('🔐 Cache corrompu, redirection login');
            window.location.href = '/auth?mode=login';
          }
        } else {
          console.log('🔐 Pas de cache, redirection login');
          window.location.href = '/auth?mode=login';
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    checkAuth();
    
    // Timeout de sécurité : si le chargement dure plus de 6 secondes
    const timeout = setTimeout(() => {
      if (!authComplete && isMounted) {
        console.log('⏱️ Timeout 6s - forçage arrêt loading');
        const savedUser = localStorage.getItem(USER_KEY);
        if (savedUser) {
          try {
            setCustomer(JSON.parse(savedUser));
            console.log('⏱️ Cache utilisé après timeout');
          } catch (e) {
            console.log('⏱️ Cache invalide');
          }
        }
        setIsLoading(false);
      }
    }, 6000);
    
    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, []);

  // Déconnexion
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('heartbeats_age_verified');
    localStorage.removeItem('heartbeats_rgpd_consent');
    toast.success('Déconnexion réussie 👋');
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  };

  // Écran de chargement cosmique
  if (isLoading) {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] flex items-center justify-center" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        {/* Étoiles scintillantes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
        
        <motion.div
          className="text-center relative z-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Coeur pulsant */}
          <div className="relative mb-6">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <Heart 
                className="w-20 h-20 text-pink-500 mx-auto" 
                fill="currentColor"
                style={{ filter: 'drop-shadow(0 0 20px rgba(244, 63, 94, 0.6))' }}
              />
            </motion.div>
            
            {/* Orbite */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <div className="absolute -top-2 left-1/2 w-3 h-3 bg-purple-400 rounded-full" 
                   style={{ filter: 'blur(1px)' }} />
            </motion.div>
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">Heartbeats</h2>
          <p className="text-purple-300/70">Connexion en cours...</p>
          
          {/* Barre de progression */}
          <div className="mt-6 w-48 h-1 bg-white/10 rounded-full overflow-hidden mx-auto">
            <motion.div
              className="h-full bg-gradient-to-r from-pink-500 to-purple-500"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            />
          </div>
          
          {/* Bouton de secours après 4 secondes */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 4 }}
            className="mt-8"
          >
            <button
              onClick={() => {
                console.log('🔐 Skip manuel demandé');
                setIsLoading(false);
                // Essayer d'utiliser le cache
                const savedUser = localStorage.getItem(USER_KEY);
                if (savedUser) {
                  try {
                    setCustomer(JSON.parse(savedUser));
                  } catch (e) {
                    console.log('Cache parse error:', e);
                  }
                }
              }}
              className="text-sm text-purple-300/50 hover:text-purple-300 underline transition-colors"
            >
              Chargement trop long ? Cliquer ici
            </button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Pas d'utilisateur authentifié
  if (!customer) {
    return (
      <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] flex items-center justify-center" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
        <div className="text-center text-white">
          <Heart className="w-16 h-16 text-pink-500 mx-auto mb-4" fill="currentColor" />
          <h2 className="text-xl font-bold mb-2">Session expirée</h2>
          <p className="text-white/60 mb-4">Reconnecte-toi pour continuer</p>
          <button
            onClick={() => window.location.href = '/auth?mode=login'}
            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            Se reconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] text-white" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      
      {/* Header compact */}
      <header className="sticky top-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Heart className="w-7 h-7 text-pink-500" fill="currentColor" />
            <span className="text-lg font-black tracking-tight">Heartbeats</span>
          </div>
          
          {/* User Menu */}
          <div className="flex items-center gap-3">
            {/* 🌍 Sélecteur de langue */}
            <LanguageSelector variant="minimal" />
            
            {/* Avatar / Nom */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-sm font-bold">
                {customer.firstName?.[0] || customer.name?.[0] || '?'}
              </div>
              <span className="text-sm font-medium hidden sm:block">
                {customer.firstName || customer.name || 'Utilisateur'}
              </span>
            </div>
            
            {/* Bouton Déconnexion */}
            <button
              onClick={handleLogout}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              title="Déconnexion"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 text-white/60 hover:text-white" />
            </button>
          </div>
        </div>
      </header>

      {/* Module Heartbeats */}
      <main className="relative">
        <HeartbeatsModule
          customer={customer}
          isDarkMode={true}
          onClose={() => {
            // Sur heartbeats.fr, fermer = retour à la landing
            window.location.href = '/';
          }}
        />
      </main>

      {/* Toaster */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default HeartbeatsPortal;
